import { WebsiteConfig } from "@/types/WebsiteConfig";
import {
  createWebsiteConfig as createWebsiteConfigApi,
  getWebsiteConfig as getWebsiteConfigApi,
  updateWebsiteConfig as updateWebsiteConfigApi,
  deleteWebsiteConfig as deleteWebsiteConfigApi,
  websiteConfigExists as websiteConfigExistsApi,
  updateWebsiteConfigAndPage as updateWebsiteConfigAndPageApi,
  getCustomDomainMetaData as getCustomDomainMetaDataApi,
  toggleWebsiteStatus as toggleWebsiteStatusApi,
  isWebsitePublished as isWebsitePublishedApi,
  getWebsiteHealth as getWebsiteHealthApi,
  type WebsiteHealthResult,
  checkCertificateStatus as checkCertificateStatusApi,
  removeCustomDomain as removeCustomDomainApi,
  getPresignedCustomWebsiteUploadUrl as getPresignedCustomWebsiteUploadUrlApi,
  processCustomWebsiteUpload as processCustomWebsiteUploadApi,
  cancelCustomWebsiteUpload as cancelCustomWebsiteUploadApi,
  toggleCustomWebsite as toggleCustomWebsiteApi,
  setupCustomWebsite as setupCustomWebsiteApi,
  ensureWebsiteConfig as ensureWebsiteConfigApi,
  deleteCustomWebsite as deleteCustomWebsiteApi,
  downloadCustomWebsite as downloadCustomWebsiteApi,
  getCustomWebsiteStatus as getCustomWebsiteStatusApi,
  createTemplateWebsiteConfig as createTemplateWebsiteConfigApi,
  getTemplateWebsiteConfig as getTemplateWebsiteConfigApi,
  updateTemplateWebsiteConfig as updateTemplateWebsiteConfigApi,
  deleteTemplateWebsiteConfig as deleteTemplateWebsiteConfigApi,
  updateTemplateWebsiteConfigAndPage as updateTemplateWebsiteConfigAndPageApi,
  getTemplatePages as getTemplatePagesApi,
  getTemplatePage as getTemplatePageApi,
  createTemplatePage as createTemplatePageApi,
  updateTemplatePage as updateTemplatePageApi,
  deleteTemplatePage as deleteTemplatePageApi,
  duplicateTemplatePage as duplicateTemplatePageApi,
  getPresignedCustomWebsiteUploadUrlTemplate as getPresignedCustomWebsiteUploadUrlTemplateApi,
  setupCustomWebsiteTemplate as setupCustomWebsiteTemplateApi,
} from "@/lib/api/clients/WebsiteConfigClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { Page } from "@/types/Page";
import { CertificateResult } from "@/types/CertificateResult";

/**
 * Remove custom domain from the website config and CloudFront distribution
 * @param eventId Event ID
 * @returns Promise that resolves when the custom domain is removed
 */
export async function removeCustomDomain(
  eventId: string,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    await removeCustomDomainApi(eventId);
  } catch (error) {
    console.error("Error removing custom domain:", error);
    return handleErrorServer(error);
  }
}

/**
 * Check the ACM certificate status for a custom domain
 * @param eventId Event ID
 * @returns Promise resolving to the CertificateResult with certificate status
 */
export async function checkCertificateStatus(
  eventId: string,
): Promise<CertificateResult | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await checkCertificateStatusApi(eventId);
  } catch (error) {
    console.error("Error checking certificate status:", error);
    return handleErrorServer(error);
  }
}

// CFCH cutover: verifyCnameStatus removed. Cloudflare for SaaS auto-
// validates HTTP DCV — `checkCertificateStatus` is the single polling
// entry point and returns `isSiteOnline` on the same payload.

/**
 * Check if the website is published and the CloudFront distribution is deployed
 * @param eventId Event ID
 * @returns Promise resolving to a boolean indicating if the website is published and deployed
 */
export async function isWebsitePublished(
  eventId: string,
): Promise<boolean | ServerError> {
  try {
    // Input validation
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await isWebsitePublishedApi(eventId);
  } catch (error) {
    console.error("Error checking website published status:", error);
    return handleErrorServer(error);
  }
}

/**
 * Probe the public website URL to find out whether it's actually serving
 * traffic right now. Callers poll this until `live === true` to drive
 * the yellow → green status transition.
 */
export async function getWebsiteHealth(
  eventId: string,
): Promise<WebsiteHealthResult | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    return await getWebsiteHealthApi(eventId);
  } catch (error) {
    console.error("Error probing website health:", error);
    return handleErrorServer(error);
  }
}

/**
 * Toggle the website publish status (enable/disable CloudFront distribution)
 * @param eventId Event ID
 * @param published Whether the website should be published (true) or unpublished (false)
 * @returns Promise that resolves when the toggle is complete
 */
export async function toggleWebsiteStatus(
  eventId: string,
  published: boolean,
): Promise<void | ServerError> {
  try {
    // Input validation
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    if (typeof published !== "boolean") {
      throw new Error("validationError");
    }

    await toggleWebsiteStatusApi(eventId, published);
  } catch (error) {
    console.error("Error toggling website status:", error);
    return handleErrorServer(error);
  }
}

/**
 * Get or create a certificate for a custom domain
 * @param eventId Event ID
 * @param domain Custom domain to get/create certificate for
 * @returns Promise resolving to the certificate result
 */
export async function getCustomDomainMetaData(
  eventId: string,
  domain?: string,
): Promise<CertificateResult | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Only validate domain format if domain is provided
    if (domain && domain.trim() !== "") {
      const domainRegex =
        /^(([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})$/;
      if (!domainRegex.test(domain)) {
        throw new Error("validationError");
      }
    }

    return await getCustomDomainMetaDataApi(eventId, domain);
  } catch (error) {
    console.error("Error getting custom domain metadata:", error);
    return handleErrorServer(error);
  }
}

/**
 * Create a website config with validation checks
 * @param eventId Event ID
 * @param websiteConfig Website config object to create
 * @returns Promise resolving to the created website config ID
 */
export async function createWebsiteConfig(
  eventId: string,
  websiteConfig: WebsiteConfig,
  pages: Page[] = [],
  /** Art. 50 KI-VO: the AI tool that produced this site (see the client). */
  aiContentTool?: string,
): Promise<string | ServerError> {
  try {
    // Input validation
    if (!eventId || !websiteConfig || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate color formats if provided
    if (
      (websiteConfig.primaryColor &&
        !/^#[0-9A-Fa-f]{6}$/.test(websiteConfig.primaryColor)) ||
      (websiteConfig.secondaryColor &&
        !/^#[0-9A-Fa-f]{6}$/.test(websiteConfig.secondaryColor)) ||
      (websiteConfig.backgroundColor &&
        !/^#[0-9A-Fa-f]{6}$/.test(websiteConfig.backgroundColor)) ||
      // Validate cookie-related color formats if provided
      (websiteConfig.colorCookieHeader &&
        !/^#[0-9A-Fa-f]{6}$/.test(websiteConfig.colorCookieHeader)) ||
      (websiteConfig.colorCookieBody &&
        !/^#[0-9A-Fa-f]{6}$/.test(websiteConfig.colorCookieBody)) ||
      (websiteConfig.backgroundCookieColor &&
        !/^#[0-9A-Fa-f]{6}$/.test(websiteConfig.backgroundCookieColor))
    ) {
      throw new Error("validationError");
    }

    // Validate new cookie fields
    if (
      typeof websiteConfig.cookieHeaderText === "string" &&
      websiteConfig.cookieHeaderText.length > 200
    ) {
      throw new Error("validationError");
    }
    if (
      typeof websiteConfig.cookieBodyText === "string" &&
      websiteConfig.cookieBodyText.length > 1000
    ) {
      throw new Error("validationError");
    }
    if (
      websiteConfig.cookiePolicyUrl &&
      websiteConfig.cookiePolicyUrl.trim() !== "" &&
      !/^https?:\/\/.+/.test(websiteConfig.cookiePolicyUrl)
    ) {
      throw new Error("validationError");
    }
    // Legal-line URLs: same shape rule as the cookie policy URL (backend twin:
    // `WebsiteConfigValidation`, @Pattern + 500 cap, the number `errors.imprintUrlTooLong` names).
    for (const legalUrl of [websiteConfig.imprintUrl, websiteConfig.privacyPolicyUrl]) {
      if (
        legalUrl &&
        legalUrl.trim() !== "" &&
        (!/^https?:\/\/.+/.test(legalUrl) || legalUrl.length > 500)
      ) {
        throw new Error("validationError");
      }
    }
    if (
      websiteConfig.cookieEnabled !== undefined &&
      typeof websiteConfig.cookieEnabled !== "boolean"
    ) {
      throw new Error("validationError");
    }

    // Validate homeSlug format if provided
    if (
      websiteConfig.homeSlug &&
      !/^\/[a-z0-9\-_\/]*$/.test(websiteConfig.homeSlug)
    ) {
      throw new Error("validationError");
    }

    // Validate each page
    pages.forEach((page) => {
      if (!page.pageName) throw new Error("validationError");
      if (!page.slug) throw new Error("validationError");
      if (!page.websiteJson) throw new Error("validationError");

      // Check for slug format - must start with a slash followed by alphanumeric characters, hyphens, or underscores
      if (page.slug && !/^\/[a-z0-9\-_\/]*$/.test(page.slug))
        throw new Error("validationError");
    });

    // Check for duplicate slugs within the pages array
    const slugs = new Set<string>();
    const duplicateSlugs = pages
      .map((page) => page.slug)
      .filter((slug) => {
        if (slugs.has(slug!)) {
          return true;
        }
        slugs.add(slug!);
        return false;
      });

    if (duplicateSlugs?.length > 0) throw new Error("validationError");

    // Sanitize customCss - strip <script> tags and expression()
    if (websiteConfig.customCss) {
      websiteConfig.customCss = websiteConfig.customCss
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/expression\s*\(/gi, "");
    }


    // Create the website config
    return await createWebsiteConfigApi(
      eventId,
      websiteConfig,
      pages,
      aiContentTool,
    );
  } catch (error) {
    console.error("Error creating website config:", error);
    return handleErrorServer(error);
  }
}

/**
 * Delete a website config for an event
 * @param eventId Event ID
 * @returns Promise that resolves when the website config is deleted
 */
export async function deleteWebsiteConfig(
  eventId: string,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    await deleteWebsiteConfigApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Update a website config with validation checks
 * @param eventId Event ID
 * @param websiteConfig Website config object with updated values
 * @returns Promise that resolves when the update is complete
 */
export async function updateWebsiteConfig(
  eventId: string,
  websiteConfig: WebsiteConfig,
  expectedVersion?: number,
): Promise<void | ServerError> {
  try {
    // Input validation
    if (!eventId || !websiteConfig || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Check if at least one field is being updated
    if (
      !websiteConfig.bodyFont &&
      !websiteConfig.headerFont &&
      !websiteConfig.primaryColor &&
      !websiteConfig.secondaryColor &&
      !websiteConfig.backgroundColor &&
      !websiteConfig.rounding &&
      !websiteConfig.appBar &&
      !websiteConfig.footer &&
      !websiteConfig.homeSlug &&
      websiteConfig.published === undefined &&
      !websiteConfig.favIcon &&
      !websiteConfig.touchIcon &&
      // Include cookie-related properties in update check
      !websiteConfig.colorCookieHeader &&
      !websiteConfig.fontCookieHeader &&
      !websiteConfig.colorCookieBody &&
      !websiteConfig.fontCookieBody &&
      !websiteConfig.backgroundCookieColor &&
      // New theme fields
      !websiteConfig.bodyFontColor &&
      !websiteConfig.bodyFontSize &&
      !websiteConfig.bodyLayoutOption &&
      !websiteConfig.h1FontFamily &&
      !websiteConfig.h1FontSize &&
      !websiteConfig.h1FontColor &&
      !websiteConfig.h2FontFamily &&
      !websiteConfig.h2FontSize &&
      !websiteConfig.h2FontColor &&
      !websiteConfig.primaryBtnBgColor &&
      !websiteConfig.primaryBtnBgHoverColor &&
      !websiteConfig.primaryBtnBorderColor &&
      !websiteConfig.primaryBtnBorderHoverColor &&
      !websiteConfig.primaryBtnFontColor &&
      !websiteConfig.primaryBtnFontHoverColor &&
      !websiteConfig.primaryBtnFontSize &&
      !websiteConfig.primaryBtnFontHoverSize &&
      !websiteConfig.primaryBtnBorderRadius &&
      !websiteConfig.primaryBtnHoverBorderRadius &&
      !websiteConfig.secondaryBtnBgColor &&
      !websiteConfig.secondaryBtnBgHoverColor &&
      !websiteConfig.secondaryBtnBorderColor &&
      !websiteConfig.secondaryBtnBorderHoverColor &&
      !websiteConfig.secondaryBtnFontColor &&
      !websiteConfig.secondaryBtnFontHoverColor &&
      !websiteConfig.secondaryBtnFontSize &&
      !websiteConfig.secondaryBtnFontHoverSize &&
      !websiteConfig.secondaryBtnBorderRadius &&
      !websiteConfig.secondaryBtnHoverBorderRadius &&
      !websiteConfig.hyperlinkFontColor &&
      !websiteConfig.hyperlinkFontHoverColor &&
      websiteConfig.hyperlinkUnderline === undefined &&
      !websiteConfig.customCss &&
      !websiteConfig.openGraphTitle &&
      !websiteConfig.openGraphDescription &&
      !websiteConfig.openGraphImageUrl &&
      !websiteConfig.senderEmail &&
      websiteConfig.loginAfterRegister === undefined &&
      !websiteConfig.notifyEmail &&
      !websiteConfig.cookieHeaderText &&
      !websiteConfig.cookieBodyText &&
      websiteConfig.cookieEnabled === undefined &&
      !websiteConfig.cookiePolicyUrl &&
      // `=== undefined`: CLEARING a legal URL ("") is a real change — it puts
      // the site's legal line back on the platform's page.
      websiteConfig.imprintUrl === undefined &&
      websiteConfig.privacyPolicyUrl === undefined &&
      // Login block fields
      websiteConfig.loginBlockEnabled === undefined &&
      !websiteConfig.loginBlockConditions &&
      !websiteConfig.loginBlockMessage &&
      // Resubmit block fields (toggle + exemption conditions + message).
      // Without these here the "is anything dirty?" guard would 400 a
      // resubmit-only save with `validationError` before any network
      // request is even issued — UI then shows the generic "Fehler
      // beim Aktualisieren" with nothing in DevTools to trace.
      websiteConfig.resubmitBlockEnabled === undefined &&
      !websiteConfig.resubmitBlockConditions &&
      !websiteConfig.resubmitBlockMessage &&
      // Dedupe gate (toggle + match conditions). Same omission as the
      // resubmit block above had: without these a dedupe-only save trips
      // the guard and throws `validationError` before any PUT is issued.
      websiteConfig.dedupeEnabled === undefined &&
      !websiteConfig.dedupeConditions &&
      // Automatic head-count lockdown, change deadline and segment windows.
      // Third repetition of the omission the two comments above describe.
      // `=== undefined` where a FALSY value is meaningful: `false` turns the
      // lock off and `""` is the only way to erase a stored deadline — a
      // `!x` test would swallow both and leave the user with no way back.
      websiteConfig.loginBlockThresholdEnabled === undefined &&
      websiteConfig.loginBlockThresholdCount === undefined &&
      !websiteConfig.loginBlockThresholdConditions &&
      !websiteConfig.loginBlockThresholdStatuses &&
      websiteConfig.resubmitDeadline === undefined &&
      // An empty array is truthy, so `!x` correctly treats "delete the last
      // window" as a real save.
      !websiteConfig.registrationWindows &&
      // Schedule fields
      !websiteConfig.availableFrom &&
      !websiteConfig.availableUntil &&
      // Search-engine opt-out (B-6): a noindex-only save is a real save.
      websiteConfig.noindex === undefined
    ) {
      throw new Error("validationError");
    }

    // Validate color formats if provided (supports 6 or 8 hex digits)
    const hexColorRegex = /^#[0-9A-Fa-f]{6,8}$/;
    const colorFields: (keyof WebsiteConfig)[] = [
      "primaryColor",
      "secondaryColor",
      "backgroundColor",
      "colorCookieHeader",
      "colorCookieBody",
      "backgroundCookieColor",
      "bodyFontColor",
      "h1FontColor",
      "h2FontColor",
      "primaryBtnBgColor",
      "primaryBtnBgHoverColor",
      "primaryBtnBorderColor",
      "primaryBtnBorderHoverColor",
      "primaryBtnFontColor",
      "primaryBtnFontHoverColor",
      "secondaryBtnBgColor",
      "secondaryBtnBgHoverColor",
      "secondaryBtnBorderColor",
      "secondaryBtnBorderHoverColor",
      "secondaryBtnFontColor",
      "secondaryBtnFontHoverColor",
      "hyperlinkFontColor",
      "hyperlinkFontHoverColor",
    ];
    for (const field of colorFields) {
      const value = websiteConfig[field];
      if (
        value &&
        typeof value === "string" &&
        value !== "transparent" &&
        value !== "inherit" &&
        !hexColorRegex.test(value)
      ) {
        throw new Error("validationError");
      }
    }

    // Validate new cookie fields
    if (
      typeof websiteConfig.cookieHeaderText === "string" &&
      websiteConfig.cookieHeaderText.length > 200
    ) {
      throw new Error("validationError");
    }
    if (
      typeof websiteConfig.cookieBodyText === "string" &&
      websiteConfig.cookieBodyText.length > 1000
    ) {
      throw new Error("validationError");
    }
    if (
      websiteConfig.cookiePolicyUrl &&
      websiteConfig.cookiePolicyUrl.trim() !== "" &&
      !/^https?:\/\/.+/.test(websiteConfig.cookiePolicyUrl)
    ) {
      throw new Error("validationError");
    }
    // Legal-line URLs: same shape rule as the cookie policy URL (backend twin:
    // `WebsiteConfigValidation`, @Pattern + 500 cap, the number `errors.imprintUrlTooLong` names).
    for (const legalUrl of [websiteConfig.imprintUrl, websiteConfig.privacyPolicyUrl]) {
      if (
        legalUrl &&
        legalUrl.trim() !== "" &&
        (!/^https?:\/\/.+/.test(legalUrl) || legalUrl.length > 500)
      ) {
        throw new Error("validationError");
      }
    }
    if (
      websiteConfig.cookieEnabled !== undefined &&
      typeof websiteConfig.cookieEnabled !== "boolean"
    ) {
      throw new Error("validationError");
    }

    // Sanitize customCss - strip <script> tags and expression()
    if (websiteConfig.customCss) {
      websiteConfig.customCss = websiteConfig.customCss
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/expression\s*\(/gi, "");
    }

    // Validate homeSlug format if provided
    if (
      websiteConfig.homeSlug &&
      !/^\/[a-z0-9\-_\/]*$/.test(websiteConfig.homeSlug)
    ) {
      throw new Error("validationError");
    }

    // 2026-05-16 (audit CRIT-6): forward optimistic-concurrency
    // version to the API client. When provided the client sends
    // `If-Match: <version>`; backend returns 409 `versionMismatch`
    // on stale writes which the cockpit surfaces as a "Settings
    // changed in another tab — reload to continue" prompt.
    await updateWebsiteConfigApi(eventId, websiteConfig, expectedVersion);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Save a SPARSE gate/meta patch, re-reading the version once if the
 * If-Match precondition was stale.
 *
 * 2026-08-13. `version` is the optimistic-concurrency token for USER edits,
 * but machine writes spend it too: `GET /certificate-status` persists the
 * Cloudflare mirror (`cfHostnameStatus`, `cfHostnameVerification`,
 * `siteTheoreticallyOnCustomDomain`) and every persisted mutation `$inc`s
 * the stored version — even when no value changed. The settings modal mounts
 * ALL of its tabs at once (`PWebsiteSettings`, audit P2.12), so opening it on
 * an event with a custom domain fires that probe and moves the version out
 * from under the copy the cockpit is holding. Result: a 409 on every save,
 * with a "changed in another tab" message naming a tab that doesn't exist,
 * and a reload that cannot help because the next open probes again.
 *
 * Retrying blind is only safe because of WHAT is being sent. The payloads
 * routed through here are sparse patches carrying exclusively the fields the
 * user touched (see `WebsiteStatus.handleSave` / `SharingCookies`), and those
 * fields are disjoint from the machine-owned Cloudflare mirror — so a
 * mismatch here means "someone bumped the counter", not "someone edited what
 * you are editing". There is no conflict to resolve, only a token to refresh.
 *
 * The website BUILDER deliberately does NOT use this: its payload carries the
 * whole appBar/footer/theme bundle, where a second tab's save IS a real
 * conflict and last-write-wins would silently eat it. It keeps the plain
 * guarded call and surfaces the 409.
 *
 * Exactly ONE retry. Two mismatches in a row is no longer a bumped counter —
 * that is a genuine concurrent writer, and the user gets the reload prompt.
 *
 * Returns the version the accepted write was guarded with, so the caller can
 * advance its cached copy to `written + 1` (the backend `$inc`s by one on
 * success) instead of blind-bumping a number that was already wrong.
 */
export async function updateWebsiteConfigWithFreshVersion(
  eventId: string,
  websiteConfig: WebsiteConfig,
  expectedVersion?: number,
): Promise<{ writtenVersion?: number } | ServerError> {
  const first = await updateWebsiteConfig(eventId, websiteConfig, expectedVersion);
  if (!isVersionMismatch(first)) {
    return first ?? { writtenVersion: expectedVersion };
  }

  // An unguarded save cannot produce `versionMismatch`, so there is nothing
  // to refresh — and re-sending would be a second unguarded write.
  if (expectedVersion === undefined || expectedVersion === null) return first;

  const fresh = await getWebsiteConfig(eventId, "version");
  if (!fresh || "isServerError" in fresh || typeof fresh.version !== "number") {
    // Could not learn the current version — surface the ORIGINAL mismatch so
    // the user still gets the reload prompt rather than a read error for a
    // save they attempted.
    return first;
  }

  const second = await updateWebsiteConfig(eventId, websiteConfig, fresh.version);
  return second ?? { writtenVersion: fresh.version };
}

/** A ServerError carrying the backend's 409 optimistic-concurrency code. */
function isVersionMismatch(result: void | ServerError): result is ServerError {
  return (
    !!result &&
    typeof result === "object" &&
    "isServerError" in result &&
    result.isServerError === true &&
    result.message === "versionMismatch"
  );
}

/**
 * Get a website config for an event with validation checks.
 *
 * Returns `null` when no config doc exists for the event (post-stage-A
 * backend returns 200 + empty body for that case). Pre-stage-A backend
 * 404s — that surfaces as ServerError. All callers handle both so the
 * deploy order is safe in either direction.
 */
export async function getWebsiteConfig(
  eventId: string,
  fields?: string,
): Promise<WebsiteConfig | null | ServerError> {
  try {
    // Input validation
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await getWebsiteConfigApi(eventId, fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Check if a website config exists for an event
 * @param eventId Event ID
 * @returns Promise resolving to a boolean indicating if the website config exists
 */
export async function websiteConfigExists(
  eventId: string,
): Promise<boolean | ServerError> {
  try {
    // Input validation
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await websiteConfigExistsApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * 2026-09-28: create the website_config row (unpublished shell) when the event
 * has none, so the settings dialog can open from the empty state. Idempotent.
 */
export async function ensureWebsiteConfig(
  eventId: string,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    await ensureWebsiteConfigApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Full setup for custom website upload from the empty state.
 * Creates a minimal config (if none exists), processes the uploaded ZIP,
 * and activates custom website mode — all in a single backend call.
 */
export async function setupCustomWebsite(
  eventId: string,
  fileName: string,
  publishNow: boolean,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    if (!fileName) {
      throw new Error("validationError");
    }

    await setupCustomWebsiteApi(eventId, fileName, publishNow);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initialize website config data or create a default one if it doesn't exist
 * @param eventId Event ID
 * @returns Promise resolving to the website config
 */
export async function initializeWebsiteConfig(
  eventId: string,
): Promise<WebsiteConfig | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }


    // If it exists, get the website config data
    const result = await getWebsiteConfigApi(eventId);
    if (result === null) return handleErrorServer(new Error("websiteConfigNotFound"));
    return result;
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Update both a website config and a page in a single operation
 * @param eventId Event ID
 * @param websiteConfig Website config object with updated values
 * @param page Page object with updated values
 * @returns Promise that resolves when the update is complete
 */
export async function updateWebsiteConfigAndPage(
  eventId: string,
  websiteConfig: WebsiteConfig,
  page: Page,
  /** Art. 50 KI-VO: the AI tool behind this write (see the client). */
  aiContentTool?: string,
): Promise<void | ServerError> {
  try {
    if (
      !eventId ||
      !websiteConfig ||
      !page ||
      !page.pageId ||
      !isValidObjectId(eventId) ||
      !isValidObjectId(page.pageId)
    ) {
      throw new Error("validationError");
    }

    // Check if at least one website config field is being updated
    const hasAnyField =
      websiteConfig.appBar ||
      websiteConfig.footer ||
      websiteConfig.colorCookieHeader ||
      websiteConfig.fontCookieHeader ||
      websiteConfig.colorCookieBody ||
      websiteConfig.fontCookieBody ||
      websiteConfig.backgroundCookieColor ||
      websiteConfig.bodyFontColor ||
      websiteConfig.bodyFontSize ||
      websiteConfig.bodyLayoutOption ||
      websiteConfig.h1FontFamily ||
      websiteConfig.h1FontSize ||
      websiteConfig.h1FontColor ||
      websiteConfig.h2FontFamily ||
      websiteConfig.h2FontSize ||
      websiteConfig.h2FontColor ||
      websiteConfig.primaryBtnBgColor ||
      websiteConfig.primaryBtnBgHoverColor ||
      websiteConfig.primaryBtnBorderColor ||
      websiteConfig.primaryBtnBorderHoverColor ||
      websiteConfig.primaryBtnFontColor ||
      websiteConfig.primaryBtnFontHoverColor ||
      websiteConfig.primaryBtnFontSize ||
      websiteConfig.primaryBtnFontHoverSize ||
      websiteConfig.primaryBtnBorderRadius ||
      websiteConfig.primaryBtnHoverBorderRadius ||
      websiteConfig.secondaryBtnBgColor ||
      websiteConfig.secondaryBtnBgHoverColor ||
      websiteConfig.secondaryBtnBorderColor ||
      websiteConfig.secondaryBtnBorderHoverColor ||
      websiteConfig.secondaryBtnFontColor ||
      websiteConfig.secondaryBtnFontHoverColor ||
      websiteConfig.secondaryBtnFontSize ||
      websiteConfig.secondaryBtnFontHoverSize ||
      websiteConfig.secondaryBtnBorderRadius ||
      websiteConfig.secondaryBtnHoverBorderRadius ||
      websiteConfig.hyperlinkFontColor ||
      websiteConfig.hyperlinkFontHoverColor ||
      websiteConfig.hyperlinkUnderline !== undefined ||
      websiteConfig.customCss ||
      websiteConfig.cookieHeaderText ||
      websiteConfig.cookieBodyText ||
      websiteConfig.cookieEnabled !== undefined ||
      websiteConfig.cookiePolicyUrl ||
      websiteConfig.imprintUrl !== undefined ||
      websiteConfig.privacyPolicyUrl !== undefined;

    if (!hasAnyField) {
      throw new Error("validationError");
    }

    // Validate color formats if provided
    const hexColorRegex = /^#[0-9A-Fa-f]{6,8}$/;
    const colorFieldsToValidate: (keyof WebsiteConfig)[] = [
      "colorCookieHeader",
      "colorCookieBody",
      "backgroundCookieColor",
      "bodyFontColor",
      "h1FontColor",
      "h2FontColor",
      "primaryBtnBgColor",
      "primaryBtnBgHoverColor",
      "primaryBtnBorderColor",
      "primaryBtnBorderHoverColor",
      "primaryBtnFontColor",
      "primaryBtnFontHoverColor",
      "secondaryBtnBgColor",
      "secondaryBtnBgHoverColor",
      "secondaryBtnBorderColor",
      "secondaryBtnBorderHoverColor",
      "secondaryBtnFontColor",
      "secondaryBtnFontHoverColor",
      "hyperlinkFontColor",
      "hyperlinkFontHoverColor",
    ];
    for (const field of colorFieldsToValidate) {
      const value = websiteConfig[field];
      if (
        value &&
        typeof value === "string" &&
        value !== "transparent" &&
        value !== "inherit" &&
        !hexColorRegex.test(value)
      ) {
        throw new Error("validationError");
      }
    }

    // Validate new cookie fields
    if (
      typeof websiteConfig.cookieHeaderText === "string" &&
      websiteConfig.cookieHeaderText.length > 200
    ) {
      throw new Error("validationError");
    }
    if (
      typeof websiteConfig.cookieBodyText === "string" &&
      websiteConfig.cookieBodyText.length > 1000
    ) {
      throw new Error("validationError");
    }
    if (
      websiteConfig.cookiePolicyUrl &&
      websiteConfig.cookiePolicyUrl.trim() !== "" &&
      !/^https?:\/\/.+/.test(websiteConfig.cookiePolicyUrl)
    ) {
      throw new Error("validationError");
    }
    // Legal-line URLs: same shape rule as the cookie policy URL (backend twin:
    // `WebsiteConfigValidation`, @Pattern + 500 cap, the number `errors.imprintUrlTooLong` names).
    for (const legalUrl of [websiteConfig.imprintUrl, websiteConfig.privacyPolicyUrl]) {
      if (
        legalUrl &&
        legalUrl.trim() !== "" &&
        (!/^https?:\/\/.+/.test(legalUrl) || legalUrl.length > 500)
      ) {
        throw new Error("validationError");
      }
    }
    if (
      websiteConfig.cookieEnabled !== undefined &&
      typeof websiteConfig.cookieEnabled !== "boolean"
    ) {
      throw new Error("validationError");
    }

    // Sanitize customCss
    if (websiteConfig.customCss) {
      websiteConfig.customCss = websiteConfig.customCss
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/expression\s*\(/gi, "");
    }

    if (!page.websiteJson) throw new Error("validationError");

    await updateWebsiteConfigAndPageApi(
      eventId,
      websiteConfig,
      page,
      undefined,
      aiContentTool,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Get a presigned S3 URL for uploading a custom website ZIP file
 */
export async function getPresignedCustomWebsiteUploadUrl(
  eventId: string,
  fileName: string,
  fileSize: number,
): Promise<{ uploadUrl: string } | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    if (!fileName || fileSize <= 0) {
      throw new Error("validationError");
    }

    return await getPresignedCustomWebsiteUploadUrlApi(eventId, fileName, fileSize);
  } catch (error) {
    console.error("Error getting presigned custom website upload URL:", error);
    return handleErrorServer(error);
  }
}

/**
 * Notify the backend that the custom website ZIP has been uploaded to S3
 */
export async function processCustomWebsiteUpload(
  eventId: string,
  fileName: string,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    if (!fileName) {
      throw new Error("validationError");
    }

    await processCustomWebsiteUploadApi(eventId, fileName);
  } catch (error) {
    console.error("Error processing custom website upload:", error);
    return handleErrorServer(error);
  }
}

/**
 * Cancel a staged custom-website upload by deleting the orphan R2
 * object. Idempotent on the backend (delete-missing-key = no-op), so
 * this is safe to fire-and-forget on the cockpit side. 2026-05-16
 * (audit P1.7).
 */
export async function cancelCustomWebsiteUpload(
  eventId: string,
  fileName: string,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    if (!fileName) {
      throw new Error("validationError");
    }
    await cancelCustomWebsiteUploadApi(eventId, fileName);
  } catch (error) {
    console.error("Error cancelling custom website upload:", error);
    return handleErrorServer(error);
  }
}

/**
 * Toggle between custom website and builder website
 */
export async function toggleCustomWebsite(
  eventId: string,
  customWebsiteActive: boolean,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    if (typeof customWebsiteActive !== "boolean") {
      throw new Error("validationError");
    }

    await toggleCustomWebsiteApi(eventId, customWebsiteActive);
  } catch (error) {
    console.error("Error toggling custom website:", error);
    return handleErrorServer(error);
  }
}

/**
 * Delete the custom website for an event
 */
export async function deleteCustomWebsite(
  eventId: string,
): Promise<void | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    await deleteCustomWebsiteApi(eventId);
  } catch (error) {
    console.error("Error deleting custom website:", error);
    return handleErrorServer(error);
  }
}

/**
 * Download the uploaded custom website as a ZIP blob
 */
export async function downloadCustomWebsite(
  eventId: string,
): Promise<Blob | ServerError> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await downloadCustomWebsiteApi(eventId);
  } catch (error) {
    console.error("Error downloading custom website:", error);
    return handleErrorServer(error);
  }
}

/**
 * Get the custom website status for an event
 */
export async function getCustomWebsiteStatus(
  eventId: string,
): Promise<
  | { customWebsiteUploaded: boolean; customWebsiteActive: boolean; customWebsiteFileName: string | null }
  | ServerError
> {
  try {
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await getCustomWebsiteStatusApi(eventId);
  } catch (error) {
    console.error("Error getting custom website status:", error);
    return handleErrorServer(error);
  }
}

// ── Template website config handlers ─────────────────────────────────────────

export async function createTemplateWebsiteConfigHandler(
  templateId: string,
  websiteConfig: Partial<WebsiteConfig>,
  pages: Page[] = [],
  /** J6: the event an "import from event" copies from (`?sourceEventId=`). */
  sourceEventId?: string,
): Promise<string | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!websiteConfig) throw new Error("validationError");
    if (sourceEventId !== undefined && !isValidObjectId(sourceEventId)) throw new Error("validationError");

    return await createTemplateWebsiteConfigApi(templateId, websiteConfig as WebsiteConfig, pages, sourceEventId);
  } catch (error) {
    console.error("Error creating template website config:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateWebsiteConfigHandler(
  templateId: string,
): Promise<void | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");

    await deleteTemplateWebsiteConfigApi(templateId);
  } catch (error) {
    console.error("Error deleting template website config:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateWebsiteConfigHandler(
  templateId: string,
): Promise<WebsiteConfig | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");

    return await getTemplateWebsiteConfigApi(templateId);
  } catch (error) {
    console.error("Error getting template website config:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateWebsiteConfigHandler(
  templateId: string,
  websiteConfig: WebsiteConfig,
  /** J6: the event an "import from event" copies from (`?sourceEventId=`). */
  sourceEventId?: string,
): Promise<void | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!websiteConfig) throw new Error("validationError");
    if (sourceEventId !== undefined && !isValidObjectId(sourceEventId)) throw new Error("validationError");

    await updateTemplateWebsiteConfigApi(templateId, websiteConfig, sourceEventId);
  } catch (error) {
    console.error("Error updating template website config:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateWebsiteConfigAndPageHandler(
  templateId: string,
  websiteConfig: WebsiteConfig,
  page: Page,
): Promise<void | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!websiteConfig || !page || !page.pageId || !isValidObjectId(page.pageId)) throw new Error("validationError");
    if (!page.websiteJson) throw new Error("validationError");

    await updateTemplateWebsiteConfigAndPageApi(templateId, websiteConfig, page);
  } catch (error) {
    console.error("Error updating template website config and page:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplatePagesHandler(
  templateId: string,
): Promise<Page[] | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");

    return await getTemplatePagesApi(templateId);
  } catch (error) {
    console.error("Error getting template pages:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplatePageHandler(
  templateId: string,
  slug: string,
): Promise<Page | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!slug || slug.trim().length === 0) throw new Error("validationError");

    return await getTemplatePageApi(templateId, slug);
  } catch (error) {
    console.error("Error getting template page:", error);
    return handleErrorServer(error);
  }
}

export async function createTemplatePageHandler(
  templateId: string,
  page: Pick<Page, "pageName" | "slug" | "active" | "websiteJson">,
  /** J6: the event an "import from event" copies from (`?sourceEventId=`). */
  sourceEventId?: string,
): Promise<string | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!page.slug) throw new Error("validationError");
    if (sourceEventId !== undefined && !isValidObjectId(sourceEventId)) throw new Error("validationError");

    return await createTemplatePageApi(templateId, page, sourceEventId);
  } catch (error) {
    console.error("Error creating template page:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplatePageHandler(
  templateId: string,
  pageId: string,
  page: Partial<Pick<Page, "pageName" | "slug" | "active" | "websiteJson">>,
): Promise<void | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!pageId || !isValidObjectId(pageId)) throw new Error("validationError");

    return await updateTemplatePageApi(templateId, pageId, page);
  } catch (error) {
    console.error("Error updating template page:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplatePageHandler(
  templateId: string,
  pageId: string,
): Promise<void | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!pageId || !isValidObjectId(pageId)) throw new Error("validationError");

    return await deleteTemplatePageApi(templateId, pageId);
  } catch (error) {
    console.error("Error deleting template page:", error);
    return handleErrorServer(error);
  }
}

export async function duplicateTemplatePageHandler(
  templateId: string,
  pageId: string,
): Promise<string | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) throw new Error("validationError");
    if (!pageId || !isValidObjectId(pageId)) throw new Error("validationError");

    return await duplicateTemplatePageApi(templateId, pageId);
  } catch (error) {
    console.error("Error duplicating template page:", error);
    return handleErrorServer(error);
  }
}

/**
 * Get a presigned S3 URL for uploading a custom website ZIP file for a template
 */
export async function getPresignedCustomWebsiteUploadUrlTemplate(
  templateId: string,
  fileName: string,
  fileSize: number,
): Promise<{ uploadUrl: string } | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) {
      throw new Error("validationError");
    }

    if (!fileName || fileSize <= 0) {
      throw new Error("validationError");
    }

    return await getPresignedCustomWebsiteUploadUrlTemplateApi(templateId, fileName, fileSize);
  } catch (error) {
    console.error("Error getting presigned custom website upload URL for template:", error);
    return handleErrorServer(error);
  }
}

/**
 * Full setup for custom website upload on a template.
 * Creates a minimal config (if none exists), processes the uploaded ZIP,
 * and activates custom website mode.
 */
export async function setupCustomWebsiteTemplate(
  templateId: string,
  fileName: string,
): Promise<void | ServerError> {
  try {
    if (!templateId || !isValidObjectId(templateId)) {
      throw new Error("validationError");
    }
    if (!fileName) {
      throw new Error("validationError");
    }

    await setupCustomWebsiteTemplateApi(templateId, fileName);
  } catch (error) {
    return handleErrorServer(error);
  }
}
